import { html, nothing } from "lit";
import type { EventLogEntry } from "../app-events.ts";
import type { FleetStats } from "../app-view-state.ts";
import type { RunTimelineEntry } from "../operator-trust/state.ts";
import type { SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { renderChatActivityPanel, type ChatActivityPanelProps } from "./chat-activity-panel.ts";
import { renderChatRunTimeline } from "./chat-run-timeline.ts";

function payloadPreview(payload: unknown): string {
  if (payload == null) return "";
  if (typeof payload === "string") return payload;
  try {
    const json = JSON.stringify(payload);
    return json.length > 120 ? json.slice(0, 117) + "..." : json;
  } catch {
    return "";
  }
}

export type FleetAnalyticsEventScope = {
  /** Session key the event log should be filtered to (null = all sessions). */
  scopeSessionKey: string | null;
  /** Per-session gateway event rings (preferred when slicing by session). */
  eventsBySession?: Record<string, EventLogEntry[]>;
  /** Flat fallback when no session slicing is desired. */
  eventsFlat?: EventLogEntry[];
  /** Cap on rendered rows; defaults to 12. */
  limit?: number;
};

export type FleetV2AnalyticsTab = "health" | "activity";

export type FleetV2AnalyticsPanelProps = {
  activeTab: FleetV2AnalyticsTab;
  onTabChange: (tab: FleetV2AnalyticsTab) => void;
  /** When true, skip Activity|Health tab chrome (Inspector already owns those tabs). */
  hideTabStrip?: boolean;
  connected: boolean;
  sending: boolean;
  stream: string | null;
  sessions: SessionsListResult | null;
  subagents: ChatSubagentCard[];
  fleetTodos: number;
  activity?: ChatActivityPanelProps;
  resetNodeBusy?: boolean;
  onResetNode?: () => void;
  events?: FleetAnalyticsEventScope;
  sessionHealth?: "healthy" | "suspect" | "wedged" | null;
  sessionHealthReason?: string | null;
  turnPhase?: string | null;
  runTimeline?: RunTimelineEntry[];
  fleetStats?: FleetStats | null;
};

const FLEET_V2_ANALYTICS_TABS: Array<{ id: FleetV2AnalyticsTab; label: string }> = [
  { id: "activity", label: "Activity" },
  { id: "health", label: "Health" },
];

function confidenceScore(props: FleetV2AnalyticsPanelProps): number {
  if (!props.connected) {
    return 48;
  }
  if (props.sessionHealth === "wedged") {
    return 18;
  }
  if (props.sessionHealth === "suspect") {
    return 52;
  }
  const runCount = props.subagents.filter((entry) => entry.status === "running").length;
  const errorCount = props.subagents.filter((entry) => entry.status === "error").length;
  const overloaded = props.fleetStats?.overloadedAgents ?? 0;
  const score = 88 - errorCount * 8 - overloaded * 10 + Math.min(runCount, 4) * 2;
  return Math.max(12, Math.min(99, score));
}

function sessionHealthLabel(props: FleetV2AnalyticsPanelProps): string {
  if (!props.connected) {
    return "Gateway disconnected";
  }
  if (props.sessionHealth === "wedged") {
    return props.sessionHealthReason?.trim() || "Session wedged — try Abort or Reset Node";
  }
  if (props.sessionHealth === "suspect") {
    return props.sessionHealthReason?.trim() || "Session slow — still working";
  }
  if (props.turnPhase) {
    return `Turn ${props.turnPhase.replace(/_/g, " ")}`;
  }
  return props.fleetTodos > 0 ? `${props.fleetTodos} operator to-dos pending` : "Session healthy";
}

function trendBuckets(subagents: ChatSubagentCard[]): number[] {
  const buckets = new Array<number>(8).fill(0);
  const now = Date.now();
  const windowMs = 8 * 60 * 1000;
  for (const card of subagents) {
    const stamp = card.updatedAt ?? card.createdAt;
    const age = now - stamp;
    if (age < 0 || age > windowMs) {
      continue;
    }
    const bucket = 7 - Math.floor((age / windowMs) * 8);
    const safeBucket = Math.max(0, Math.min(7, bucket));
    buckets[safeBucket] += 1;
  }
  return buckets;
}

function renderHealthTab(props: FleetV2AnalyticsPanelProps) {
  const running = props.subagents.filter((entry) => entry.status === "running").length;
  const queued = props.subagents.filter(
    (entry) => entry.status === "queued" || entry.status === "spawning",
  ).length;
  const healthy = confidenceScore(props);
  const trends = trendBuckets(props.subagents);
  const trendMax = Math.max(1, ...trends);
  const alertText = sessionHealthLabel(props);
  const streamActive = props.sending || Boolean(props.stream);
  const alertWarn =
    !props.connected ||
    props.sessionHealth === "wedged" ||
    props.sessionHealth === "suspect" ||
    props.fleetTodos > 0;

  return html`
    <div class="fleet-v2-analytics__health">
      <div class="fleet-v2-analytics__kpis">
        <article class="fleet-v2-analytics__kpi">
          <span class="fleet-v2-analytics__label">Fleet utilization</span>
          <strong class="fleet-v2-analytics__value">${running}</strong>
          <span class="fleet-v2-analytics__sub">running</span>
        </article>
        <article class="fleet-v2-analytics__kpi">
          <span class="fleet-v2-analytics__label">Queue pressure</span>
          <strong class="fleet-v2-analytics__value">${queued}</strong>
          <span class="fleet-v2-analytics__sub">queued</span>
        </article>
        <article class="fleet-v2-analytics__kpi">
          <span class="fleet-v2-analytics__label">Fleet sessions</span>
          <strong class="fleet-v2-analytics__value"
            >${props.fleetStats?.totalActiveSessions ?? props.sessions?.count ?? 0}</strong
          >
          <span class="fleet-v2-analytics__sub"
            >${props.fleetStats?.overloadedAgents ?? 0} overloaded</span
          >
        </article>
        <article class="fleet-v2-analytics__kpi">
          <span class="fleet-v2-analytics__label">System confidence</span>
          <strong class="fleet-v2-analytics__value">${healthy}%</strong>
          <span class="fleet-v2-analytics__sub">${streamActive ? "streaming" : "stable"}</span>
        </article>
      </div>
      <div class="fleet-v2-analytics__trend">
        <div class="fleet-v2-analytics__trend-head">
          <span>Recent run activity</span>
          <span>${streamActive ? "Live" : "Idle"}</span>
        </div>
        <div class="fleet-v2-analytics__bars" aria-label="Recent run activity trend">
          ${trends.map(
            (value) => html`
              <span style=${`height:${Math.max(12, Math.round((value / trendMax) * 100))}%`}></span>
            `,
          )}
        </div>
      </div>
      <div class="fleet-v2-analytics__alerts">
        <div
          class="fleet-v2-analytics__alert ${
            alertWarn ? "fleet-v2-analytics__alert--warn" : "fleet-v2-analytics__alert--ok"
          }"
        >
          ${alertText}
        </div>
      </div>
      ${renderChatRunTimeline({
        entries: props.runTimeline ?? [],
        limit: 6,
        title: "Run timeline",
      })}
      ${
        props.onResetNode
          ? html`
              <div class="fleet-v2-analytics__reset-row">
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  ?disabled=${!props.connected || props.resetNodeBusy}
                  title="Stop lingering subagents, sweep orphan sessions, and abort stuck chat runs on this gateway"
                  @click=${props.onResetNode}
                >
                  ${props.resetNodeBusy ? "Resetting…" : "Reset Node"}
                </button>
                <span class="muted" style="font-size:11px;">
                  Clears lingering subagent sessions and stuck runs without deleting chat
                  transcripts.
                </span>
              </div>
            `
          : nothing
      }
    </div>
  `;
}

/**
 * Translate a raw gateway event name into plain language.
 *
 * Operators were reading wire vocabulary (`agentmainfork`, `term-finalized`)
 * and having to guess what it meant. Unmapped names fall through unchanged so
 * a newly added event is still findable — this improves the common cases
 * rather than pretending to cover everything.
 */
const GATEWAY_EVENT_LABELS: Record<string, string> = {
  agentmainfork: "Subagent session started",
  "agent-main-fork": "Subagent session started",
  "term-finalized": "Run finished and released",
  "term-finalize": "Run finished and released",
  "term-complete": "Run completed",
  "term-end": "Run completed",
  "chat.run": "Run registered",
  "chat.final": "Run finished",
  "chat.inject": "Message injected",
  "chat.abort": "Run stopped",
  "session.health": "Session health updated",
  "chat.turn": "Run phase changed",
  "operator.timeline": "Timeline milestone",
  "fleet.event": "Fleet status changed",
  "agent:spawned": "Subagent spawned",
  "agent:status": "Subagent status changed",
  "agent:completed": "Subagent completed",
  "agent:error": "Subagent failed",
  "sessions.turns.linkChild": "Subagent linked to parent",
  "subagents.list": "Subagent registry read",
};

export function describeGatewayEvent(event: string): string {
  const raw = (event ?? "").trim();
  return GATEWAY_EVENT_LABELS[raw] ?? raw;
}

function renderEventsLog(events: FleetAnalyticsEventScope | undefined) {
  if (!events) {
    return nothing;
  }
  const limit = events.limit ?? 12;
  const scopeKey = events.scopeSessionKey?.trim() || null;
  type Row = { sessionKey: string | null; entry: EventLogEntry };
  let rows: Row[] = [];
  if (events.eventsBySession && Object.keys(events.eventsBySession).length > 0) {
    for (const [sessionKey, slice] of Object.entries(events.eventsBySession)) {
      for (const entry of slice) {
        rows.push({ sessionKey, entry });
      }
    }
    rows.sort((a, b) => (b.entry.ts ?? 0) - (a.entry.ts ?? 0));
  } else if (events.eventsFlat) {
    rows = events.eventsFlat.map((entry) => ({ sessionKey: scopeKey, entry }));
  }
  if (rows.length === 0) {
    return html`
      <p class="muted fleet-v2-analytics__empty">
        No gateway events for ${scopeKey ? "this session" : "the inspector"} yet.
      </p>
    `;
  }
  const trimmed = rows.slice(0, limit);
  return html`
    <details class="fleet-v2-analytics__debug" data-testid="analytics-debug-events">
      <summary class="fleet-v2-analytics__debug-summary">
        <span>Debug</span>
        <span class="muted"> — raw gateway events</span>
      </summary>
      <div class="fleet-v2-analytics__events" aria-label="Recent gateway events">
        <div class="fleet-v2-analytics__events-head">
          <span>Recent gateway events</span>
          <span class="muted"
            >${scopeKey ? "session: " + scopeKey : "all sessions"} ·
            ${trimmed.length}/${rows.length}</span
          >
        </div>
        <ul class="fleet-v2-analytics__events-list">
          ${trimmed.map(
            ({ sessionKey, entry }) => html`
              <li class="fleet-v2-analytics__events-row">
                <span class="fleet-v2-analytics__events-kind"
                  >${describeGatewayEvent(entry.event)}</span
                >
                ${
                  scopeKey && sessionKey && sessionKey !== scopeKey
                    ? html`<span class="fleet-v2-analytics__events-session">${sessionKey}</span>`
                    : nothing
                }
                <span class="fleet-v2-analytics__events-msg">${payloadPreview(entry.payload)}</span>
              </li>
            `,
          )}
        </ul>
      </div>
    </details>
  `;
}

function renderActivityTab(
  activity: ChatActivityPanelProps | undefined,
  events: FleetAnalyticsEventScope | undefined,
) {
  const eventsBlock = renderEventsLog(events);
  if (!activity) {
    return html`
      ${eventsBlock ?? nothing}
      <p class="muted fleet-v2-analytics__empty">
        Activity stream is unavailable for this session.
      </p>
    `;
  }
  return html`
    <div class="fleet-v2-analytics__activity">${renderChatActivityPanel(activity)}</div>
    ${eventsBlock ?? nothing}
  `;
}

export function renderFleetV2AnalyticsPanel(props: FleetV2AnalyticsPanelProps) {
  const showActivityTab = Boolean(props.activity);
  const activeTab = props.activeTab === "activity" && !showActivityTab ? "health" : props.activeTab;

  return html`
    <div class="fleet-v2-analytics ${props.hideTabStrip ? "fleet-v2-analytics--bare" : ""}">
      ${
        props.hideTabStrip
          ? nothing
          : html`
              <div
                class="fleet-v2-analytics__tabs"
                role="tablist"
                aria-label="Real-time analytics views"
              >
                ${FLEET_V2_ANALYTICS_TABS.map((tab) => {
                  if (tab.id === "activity" && !showActivityTab) {
                    return nothing;
                  }
                  return html`
                    <button
                      type="button"
                      role="tab"
                      class="fleet-v2-analytics__tab ${activeTab === tab.id ? "fleet-v2-analytics__tab--active" : ""}"
                      aria-selected=${activeTab === tab.id}
                      @click=${() => props.onTabChange(tab.id)}
                    >
                      ${tab.label}
                    </button>
                  `;
                })}
              </div>
            `
      }
      <div class="fleet-v2-analytics__panel" role="tabpanel">
        ${activeTab === "activity" ? renderActivityTab(props.activity, props.events) : renderHealthTab(props)}
      </div>
    </div>
  `;
}
